<template>
    <filters
        class="my-3"
        v-model="filters"
        @fetch="events = []; hasNextPage = false; pagination.current_page = 1; fetch();"
        :loading="loading"
        date-filter
        :default-dates="defaultDates"
        search-filter
        status-filter
        :statuses="[
            { value: true, label: 'Limpo' },
            { value: false, label: 'Bloqueado' },
        ]"
        search-label="Filtre fingerprint, IP, cidade, dispositivo, etc..."
    >
        <template #actions>
            <button
                type="button"
                class="btn-primary px-4 py-1 text-sm"
                :class="{ 'opacity-70 cursor-not-allowed': migrationLoading }"
                :disabled="migrationLoading"
                @click="triggerMigration"
            >
                <span v-if="migrationLoading">Atualizando...</span>
                <span v-else>Atualizar logs</span>
            </button>
        </template>
    </filters>

    <p
        v-if="migrationFeedback"
        class="mb-3 text-xs text-emerald-600 dark:text-emerald-300"
    >
        {{ migrationFeedback }}
    </p>

    <p class="mb-3 text-xs text-gray-500 dark:text-gray-400">
        Atualizado em: {{ formattedLastUpdatedAt }}
    </p>

    <div class="mb-3 w-64">
        <users-select
            v-model="producerFilter"
            :multiple="false"
            :return-object="true"
            @update:model-value="applyProducerFilter"
        />
    </div>

    <div
        v-if="activeQuickFilters.length"
        class="flex flex-wrap gap-2 mb-3"
    >
        <span
            v-for="quickFilter in activeQuickFilters"
            :key="quickFilter.key"
            class="inline-flex items-center space-x-2 rounded bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-800 dark:bg-blue-900/40 dark:text-blue-200"
        >
            <span>
                <strong>{{ quickFilter.title }}:</strong>
                <span class="ml-1">{{ quickFilter.value }}</span>
            </span>
            <button
                type="button"
                class="leading-none text-blue-800 transition hover:text-blue-600 dark:text-blue-200 dark:hover:text-blue-100"
                @click="removeFilter(quickFilter.key)"
                aria-label="Remover filtro"
            >
                ×
            </button>
        </span>
    </div>

    <events-chart
        :dates="aggs"
        :loading="loadingDayChart"
        :loading-failed-check="loadingFailedCheckChart"
        class="mt-4"
    />

    <table-component
        :loading="loading"
        :pagination="pagination"
        :has-next-page="hasNextPage"
        :is-empty="events.length === 0 && !loading"
        :headers="[
            'Data',
            'Análises',
            'Produto',
            'Fingerprint',
            'Device',
            'Ip',
            'Email',
            'VisitorId',
            'Vendas',
            'Cartões'
        ]"
        @next-page="nextPage"
        @prev-page="prevPage"
    >
        <template v-for="event in events" :key="event.uuid ?? event.id ?? event.cookie">
            <tr>
                <td class="table-cell-sm">
                    <button
                        type="button"
                        class="w-full text-left cursor-pointer hover:underline"
                        :class="{ 'text-blue-500 font-semibold': isDateFilterActive(event.created_at) }"
                        @click="applyDateFilter(event.created_at)"
                    >
                        {{ new Date(event.created_at).toLocaleString("pt-BR") }}
                    </button>
                </td>
                <td class="table-cell-sm">
                    <div class="flex items-center space-x-2">
                        <button
                            type="button"
                            class="flex items-center space-x-2 cursor-pointer"
                            :class="{ 'text-blue-500 font-semibold': isFilterActive('filter[status]', String(event.passed)) }"
                            @click="applyStatusFilter(event.passed)"
                        >
                            <div :class="[event.passed ? 'bg-green-400' : 'bg-red-400', 'w-3 h-3 rounded-full']"></div>
                            <span class="dark:text-white">{{ event.passed  ? 'Limpo' : '' }}</span>
                        </button>

                        <button
                            v-if="event.failed_check"
                            type="button"
                            class="cursor-pointer hover:underline"
                            :class="{ 'text-blue-500 font-semibold': isFilterActive('filter[failedCheck]', event.failed_check) }"
                            @click.stop="applyFailedCheckFilter(event.failed_check)"
                        >
                            {{ event.failed_check }}
                        </button>

                        <span
                            v-if="hasSales(event)"
                            class="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-200"
                        >
                            Venda
                        </span>

                        <cards-icon v-if="event.failed_check && event.failed_check === 'VisitorCards'" :cards="cards"/>
                    </div>
                </td>
                <td class="table-cell-sm">
                    <div class="flex space-x-2 items-center">
                        <button
                            v-if="event.product"
                            type="button"
                            v-tooltip="event.product.name"
                            class="bg-purple-500 text-xs font-semibold inline-block py-1 px-2 uppercase rounded text-white"
                            :class="{ 'ring-2 ring-blue-300 ring-offset-2 ring-offset-white dark:ring-offset-gray-900': isFilterActive('filter[productId]', String(event.product.id)) }"
                            @click="applyProductFilter(event.product)"
                        >
                            {{ event.product.name.split(' ').map(n => n[0]).join('') }}
                        </button>

                        <button
                            v-if="event.offer"
                            type="button"
                            v-tooltip="event.offer.code"
                            class="bg-indigo-500 text-xs font-semibold inline-block py-1 px-2 uppercase rounded text-white"
                            :class="{ 'ring-2 ring-blue-300 ring-offset-2 ring-offset-white dark:ring-offset-gray-900': isFilterActive('filter[offerCode]', event.offer.code) }"
                            @click="applyOfferFilter(event.offer)"
                        >
                            {{ event.offer.code }}
                        </button>
                    </div>
                </td>
                <td class="table-cell-sm">
                    <button
                        type="button"
                        v-tooltip="event.cookie"
                        class="truncate w-12 block cursor-pointer text-left hover:underline"
                        :class="{ 'text-blue-500 font-semibold': isFilterActive('filter[cookie]', event.cookie) }"
                        @click="applyCookieFilter(event.cookie)"
                    >
                        {{ event.cookie }}
                    </button>
                </td>
                <td class="table-cell-sm">
                    <button
                        type="button"
                        class="flex flex-col text-left cursor-pointer hover:underline"
                        :class="{ 'text-blue-500 font-semibold': isFilterActive('filter[userAgent]', event.user_agent) }"
                        @click="applyUserAgentFilter(event.user_agent)"
                    >
                        <div class="flex space-x-2" v-if="resolvedVisitorData(event)">
                            <browser-icon :browser="resolvedVisitorData(event).browserDetails.browserName"/>
                            <span>{{ resolvedVisitorData(event).browserDetails.browserFullVersion }}</span>
                        </div>
                        <div class="flex space-x-2" v-if="resolvedVisitorData(event)">
                            <os-icon :os="resolvedVisitorData(event).browserDetails.os"/>
                            <span>{{ resolvedVisitorData(event).browserDetails.osVersion }}</span>
                        </div>
                        <div
                            class="flex space-x-1"
                            v-if="resolvedLocation(event)"
                        >
                            <span class="truncate">{{ resolvedLocation(event).country }}</span>
                            <span
                                class="truncate w-32 block"
                                v-if="resolvedLocation(event).city"
                            >
                                {{ resolvedLocation(event).city }}
                            </span>
                        </div>
                    </button>
                </td>
                <td class="table-cell-sm">
                    <button
                        type="button"
                        v-tooltip="event.ip"
                        class="truncate w-32 block cursor-pointer text-left hover:underline"
                        :class="{ 'text-blue-500 font-semibold': isFilterActive('filter[ip]', event.ip) }"
                        @click="applyIpFilter(event.ip)"
                    >
                        {{ event.ip }}
                    </button>
                </td>
                <td class="table-cell-sm">
                    <button
                        type="button"
                        class="cursor-pointer text-left hover:underline"
                        :class="{ 'text-blue-500 font-semibold': isFilterActive('filter[email]', event.email) }"
                        @click="applyEmailFilter(event.email)"
                    >
                        {{ event.email }}
                    </button>
                </td>
                <td class="table-cell-sm">
                    <button
                        v-if="event.visitorId"
                        type="button"
                        v-tooltip="event.visitorId"
                        class="cursor-pointer text-left hover:underline"
                        :class="{ 'text-blue-500 font-semibold': isFilterActive('filter[visitorId]', event.visitorId) }"
                        @click="applyVisitorFilter(event.visitorId)"
                    >
                        {{ formatVisitorIdLabel(event.visitorId) }}
                    </button>
                    <span
                        v-else
                        class="text-gray-400 dark:text-gray-500"
                    >
                        —
                    </span>
                </td>
                <td class="table-cell-sm">
                    <div v-if="hasSales(event)" class="space-y-1 text-[11px] text-gray-600 dark:text-gray-300">
                        <div
                            v-for="sale in visibleSales(event)"
                            :key="sale.id"
                            class="flex flex-wrap items-center gap-1"
                        >
                            <span class="font-semibold text-gray-800 dark:text-gray-100">#{{ sale.id }}</span>
                            <Status
                                v-if="sale.status"
                                :status="sale.status"
                                size="sm"
                            />
                            <span
                                v-else
                                class="rounded bg-emerald-100 px-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-200"
                            >
                                {{ formatSaleStatus(sale) }}
                            </span>
                            <span>{{ formatSaleDate(sale) }}</span>
                            <span v-if="sale.amount !== undefined" class="font-semibold text-gray-700 dark:text-gray-200">{{ formatSaleAmount(sale) }}</span>
                            <span v-if="sale.payment_method">{{ formatPaymentMethod(sale) }}</span>
                            <span v-if="sale.installments && sale.installments > 1" class="text-gray-500 dark:text-gray-400">{{ formatInstallments(sale) }}</span>
                        </div>
                        <button
                            v-if="remainingSales(event) || isSalesExpanded(event.visitorId)"
                            type="button"
                            class="text-[10px] font-semibold text-blue-500 hover:underline"
                            @click="toggleSalesExpansion(event.visitorId)"
                        >
                            <span v-if="!isSalesExpanded(event.visitorId)">
                                +{{ remainingSales(event) }} vendas · ver tudo
                            </span>
                            <span v-else>ver menos</span>
                        </button>
                    </div>
                    <font-awesome-icon
                        v-else
                        icon="fa-circle-question"
                        class="text-gray-300 dark:text-gray-600"
                        v-tooltip="'Nenhuma venda associada a este visitorId'"
                    />
                </td>
                <td class="table-cell-sm">
                    <div v-if="event.cards && event.cards.length" class="space-y-1 text-[11px] text-gray-600 dark:text-gray-300">
                        <div
                            v-for="card in visibleCards(event)"
                            :key="card.pk ?? card.last_4 ?? card.first_6"
                            class="flex items-center gap-2"
                        >
                            <card-brand-icon :brand="card.brand ?? 'unknown'" />
                            <div class="flex flex-col">
                                <span class="font-semibold text-gray-800 dark:text-gray-100">{{ formatCardPan(card) }}</span>
                                <span class="text-gray-500 dark:text-gray-400">
                                    {{ card.holder ?? 'Titular desconhecido' }} · {{ formatCardTries(card) }}
                                </span>
                            </div>
                        </div>
                        <button
                            v-if="remainingCards(event) || isCardsExpanded(event)"
                            type="button"
                            class="text-[10px] font-semibold text-blue-500 hover:underline"
                            @click="toggleCardsExpansion(event)"
                        >
                            <span v-if="!isCardsExpanded(event)">
                                +{{ remainingCards(event) }} cartões · ver tudo
                            </span>
                            <span v-else>ver menos</span>
                        </button>
                    </div>
                    <font-awesome-icon
                        v-else
                        icon="fa-credit-card"
                        class="text-gray-300 dark:text-gray-600"
                        v-tooltip="'Nenhuma tentativa de cartão registrada'"
                    />
                </td>
            </tr>
        </template>
    </table-component>
</template>

<script>
    import Filters from "../../components/layouts/Filters.vue";
    import TableComponent from "../../components/layouts/table/TableComponent.vue";
    import EventsChart from "../../components/antifraud/EventsChart.vue";
    import BrowserIcon from "../../components/layouts/antifraud/BrowserIcon.vue";
    import OsIcon from "../../components/layouts/antifraud/OsIcon.vue";
    import CardsIcon from "../../components/layouts/antifraud/CardsIcon.vue";
    import CardBrandIcon from "../../components/layouts/CardBrandIcon.vue";
    import Status from "../../components/layouts/Status.vue";
    import UsersSelect from "../../components/forms/UsersSelect.vue";

    export default {
        components: {Status, CardBrandIcon, CardsIcon, OsIcon, BrowserIcon, EventsChart, TableComponent, Filters, UsersSelect},
        data() {
            return {
                defaultDates: [
                    moment().subtract(1, 'month').format('DD/MM/YYYY'),
                    moment().format('DD/MM/YYYY'),
                ],
                loading: false,
                loadingDayChart: false,
                loadingFailedCheckChart: false,
                events: [],
                hasNextPage: false,
                pagination: {
                    per_page: 35,
                    current_page: 1
                },
                aggs: {
                    day: [],
                    by_failed_check: [],
                    browser: {},
                    city: {},
                    ip: {},
                    device: {}
                },
                filters: {},
                filterLabels: {},
                migrationLoading: false,
                migrationFeedback: '',
                migrationFeedbackTimeout: null,
                visitorContext: {
                    sales: {},
                    visitor_data: {}
                },
                expandedSalesVisitors: {},
                expandedCardEvents: {},
                lastUpdatedAt: null,
                producerFilter: null
            }
        },

        computed: {
            activeQuickFilters() {
                if (!this.filters) {
                    return [];
                }

                const definitions = {
                    'filter[betweenDates]': {
                        title: 'Data',
                        format: (value) => {
                            const [from, to] = value.split(',');
                            if (!to || from === to) {
                                return from;
                            }

                            return `${from} → ${to}`;
                        }
                    },
                    'filter[status]': {
                        title: 'Status',
                        format: (value) => value === 'true' ? 'Limpo' : 'Bloqueado'
                    },
                    'filter[failedCheck]': {
                        title: 'Failed Check'
                    },
                    'filter[productId]': {
                        title: 'Produto',
                        format: (value) => `ID ${value}`
                    },
                    'filter[offerCode]': {
                        title: 'Oferta'
                    },
                    'filter[cookie]': {
                        title: 'Fingerprint'
                    },
                    'filter[ip]': {
                        title: 'IP'
                    },
                    'filter[email]': {
                        title: 'Email'
                    },
                    'filter[userAgent]': {
                        title: 'User Agent'
                    },
                    'filter[visitorId]': {
                        title: 'VisitorId'
                    },
                    'filter[producerId]': {
                        title: 'Produtor'
                    }
                };

                return Object.entries(definitions).reduce((carry, [key, config]) => {
                    const rawValue = this.filters[key];

                    if (!rawValue) {
                        return carry;
                    }

                    const labelledValue = this.filterLabels[key]
                        ?? (typeof config.format === 'function' ? config.format(rawValue) : rawValue);

                    carry.push({
                        key,
                        title: config.title,
                        value: labelledValue,
                    });

                    return carry;
                }, []);
            },

            formattedLastUpdatedAt() {
                if (!this.lastUpdatedAt) {
                    return '—';
                }

                return moment(this.lastUpdatedAt).format('DD/MM/YYYY HH:mm:ss');
            }
        },

        watch: {
            filters: {
                deep: true,
                handler(newFilters) {
                    const activeKeys = newFilters ? Object.keys(newFilters) : [];
                    const updatedLabels = { ...this.filterLabels };
                    let hasChange = false;

                    Object.keys(updatedLabels).forEach((key) => {
                        if (!activeKeys.includes(key)) {
                            delete updatedLabels[key];
                            hasChange = true;
                        }
                    });

                    if (hasChange) {
                        this.filterLabels = updatedLabels;
                    }
                }
            }
        },

        mounted() {
            this.fetch();
        },

        methods: {
            applyFilter(filterKey, value, options = {}) {
                if (value === undefined || value === null || value === '') {
                    return;
                }

                const normalizedValue = typeof value === 'string' ? value : String(value);
                const currentFilters = { ...this.filters };
                const shouldToggle = options.toggle !== false && currentFilters[filterKey] === normalizedValue;

                if (shouldToggle) {
                    delete currentFilters[filterKey];
                    const { [filterKey]: _removed, ...remainingLabels } = this.filterLabels;
                    this.filterLabels = remainingLabels;
                } else {
                    currentFilters[filterKey] = normalizedValue;
                    const label = options.label ?? normalizedValue;
                    this.filterLabels = {
                        ...this.filterLabels,
                        [filterKey]: label,
                    };
                }

                this.filters = currentFilters;
                this.resetPaginationAndFetch();
            },

            resetPaginationAndFetch() {
                this.events = [];
                this.hasNextPage = false;
                this.pagination.current_page = 1;
                this.fetch();
            },

            isFilterActive(filterKey, value) {
                if (!value) {
                    return false;
                }

                const normalizedValue = typeof value === 'string' ? value : String(value);

                return this.filters?.[filterKey] === normalizedValue;
            },

            formatSingleDayRange(date) {
                if (!date) {
                    return null;
                }

                const formatted = moment(date).format('DD/MM/YYYY');

                return `${formatted},${formatted}`;
            },

            applyDateFilter(date) {
                const range = this.formatSingleDayRange(date);

                if (!range) {
                    return;
                }

                const label = moment(date).format('DD/MM/YYYY');
                this.applyFilter('filter[betweenDates]', range, { label });
            },

            isDateFilterActive(date) {
                const range = this.formatSingleDayRange(date);

                if (!range) {
                    return false;
                }

                return this.isFilterActive('filter[betweenDates]', range);
            },

            applyStatusFilter(passed) {
                const label = passed ? 'Limpo' : 'Bloqueado';
                this.applyFilter('filter[status]', String(passed), { label });
            },

            applyFailedCheckFilter(failedCheck) {
                this.applyFilter('filter[failedCheck]', failedCheck, { label: failedCheck });
            },

            applyProductFilter(product) {
                if (!product?.id) {
                    return;
                }

                this.applyFilter('filter[productId]', String(product.id), { label: product.name });
            },

            applyOfferFilter(offer) {
                if (!offer?.code) {
                    return;
                }

                this.applyFilter('filter[offerCode]', offer.code, { label: offer.code });
            },

            applyCookieFilter(cookie) {
                this.applyFilter('filter[cookie]', cookie, { label: cookie });
            },

            applyUserAgentFilter(userAgent) {
                this.applyFilter('filter[userAgent]', userAgent, { label: userAgent });
            },

            applyIpFilter(ip) {
                this.applyFilter('filter[ip]', ip, { label: ip });
            },

            applyEmailFilter(email) {
                this.applyFilter('filter[email]', email, { label: email });
            },

            applyVisitorFilter(visitorId) {
                if (!visitorId) {
                    return;
                }

                this.applyFilter('filter[visitorId]', visitorId, { label: this.formatVisitorIdLabel(visitorId) });
            },

            applyProducerFilter(producer) {
                if (!producer?.id) {
                    delete this.filters['filter[producerId]'];
                    const { ['filter[producerId]']: _removed, ...remainingLabels } = this.filterLabels;
                    this.filterLabels = remainingLabels;
                    this.producerFilter = null;
                } else {
                    this.filters['filter[producerId]'] = String(producer.id);
                    this.filterLabels['filter[producerId]'] = producer.name || producer.email;
                }
                this.resetPaginationAndFetch();
            },

            formatVisitorIdLabel(visitorId) {
                if (!visitorId) {
                    return '—';
                }

                const [prefix] = visitorId.split('-');

                return prefix || visitorId;
            },

            formatSaleStatus(sale) {
                return sale.status_label || sale.status || 'Desconhecido';
            },

            formatSaleDate(sale) {
                if (sale.created_at_human) {
                    return sale.created_at_human;
                }

                if (!sale.created_at) {
                    return 'Data não disponível';
                }

                return moment(sale.created_at).format('DD/MM/YYYY HH:mm');
            },

            formatSaleAmount(sale) {
                if (sale.amount_formatted) {
                    return sale.amount_formatted;
                }

                if (!sale.amount && sale.amount !== 0) {
                    return 'R$ 0,00';
                }

                return 'R$ ' + (sale.amount / 100).toFixed(2).replace('.', ',');
            },

            formatPaymentMethod(sale) {
                return sale.payment_method_label || sale.payment_method || '';
            },

            formatInstallments(sale) {
                return `${sale.installments}x`;
            },

            formatCardPan(card) {
                const first = card.first_6 ? `${card.first_6.substring(0, 4)} ${card.first_6.substring(4)}` : 'XXXXXX';
                const last = card.last_4 || 'XXXX';

                return `${first} •••• ${last}`;
            },

            formatCardTries(card) {
                if (!card.tries && card.tries !== 0) {
                    return 'Tentativas desconhecidas';
                }

                return `${card.tries} tentativa${card.tries === 1 ? '' : 's'}`;
            },

            visibleSales(event) {
                const sales = this.visitorContext.sales[event.visitorId] || [];

                if (this.isSalesExpanded(event.visitorId)) {
                    return sales;
                }

                return sales.slice(0, 2);
            },

            remainingSales(event) {
                const total = (this.visitorContext.sales[event.visitorId] || []).length;

                if (this.isSalesExpanded(event.visitorId)) {
                    return 0;
                }

                return total > 2 ? total - 2 : 0;
            },

            visibleCards(event) {
                const cards = event.cards || [];

                if (this.isCardsExpanded(event)) {
                    return cards;
                }

                return cards.slice(0, 2);
            },

            remainingCards(event) {
                const total = (event.cards || []).length;

                if (this.isCardsExpanded(event)) {
                    return 0;
                }

                return total > 2 ? total - 2 : 0;
            },

            isSalesExpanded(visitorId) {
                if (!visitorId) {
                    return false;
                }

                return Boolean(this.expandedSalesVisitors[visitorId]);
            },

            toggleSalesExpansion(visitorId) {
                if (!visitorId) {
                    return;
                }

                if (this.isSalesExpanded(visitorId)) {
                    const { [visitorId]: _removed, ...rest } = this.expandedSalesVisitors;
                    this.expandedSalesVisitors = rest;
                } else {
                    this.expandedSalesVisitors = {
                        ...this.expandedSalesVisitors,
                        [visitorId]: true
                    };
                }
            },

            isCardsExpanded(event) {
                const key = this.getEventKey(event);

                if (!key) {
                    return false;
                }

                return Boolean(this.expandedCardEvents[key]);
            },

            toggleCardsExpansion(event) {
                const key = this.getEventKey(event);

                if (!key) {
                    return;
                }

                if (this.isCardsExpanded(event)) {
                    const { [key]: _removed, ...rest } = this.expandedCardEvents;
                    this.expandedCardEvents = rest;
                } else {
                    this.expandedCardEvents = {
                        ...this.expandedCardEvents,
                        [key]: true
                    };
                }
            },

            getEventKey(event) {
                if (!event) {
                    return '';
                }

                return event.uuid || event.id || event.cookie || event.visitorId || '';
            },

            hasSales(event) {
                return (this.visitorContext.sales[event.visitorId] || []).length > 0;
            },

            visitorData(event) {
                return this.visitorContext.visitor_data[event.visitorId] ?? event.visitor_data ?? null;
            },

            resolvedVisitorData(event) {
                const data = this.visitorData(event);

                if (!data || !data.browserDetails) {
                    return null;
                }

                return {
                    ...data,
                    browserDetails: {
                        browserName: data.browserDetails.browserName ?? 'unknown',
                        browserFullVersion: data.browserDetails.browserFullVersion ?? data.browserDetails.browserName ?? '—',
                        os: data.browserDetails.os ?? 'unknown',
                        osVersion: data.browserDetails.osVersion ?? data.browserDetails.os ?? '—'
                    }
                };
            },

            resolvedLocation(event) {
                const visitor = this.visitorData(event);
                const ipLocation = visitor?.ipLocation ?? {};
                const countryCode = ipLocation.country?.code ?? null;
                const cityName = ipLocation.city?.name ?? null;

                if (countryCode || cityName) {
                    return {
                        country: countryCode || '—',
                        city: cityName || ''
                    };
                }

                const orderDeviceLocation = event.orderDevice?.location ?? event.order_device?.location ?? null;

                if (orderDeviceLocation) {
                    const deviceCountry = orderDeviceLocation.country?.code
                        ?? orderDeviceLocation.country?.name
                        ?? orderDeviceLocation.country
                        ?? null;
                    const deviceCity = orderDeviceLocation.city?.name
                        ?? orderDeviceLocation.city
                        ?? null;

                    if (deviceCountry || deviceCity) {
                        return {
                            country: deviceCountry || '—',
                            city: deviceCity || '',
                        };
                    }
                }

                const orderLocation = event.orderLocation ?? event.order_location ?? null;

                if (!orderLocation) {
                    return null;
                }

                const orderCountry = orderLocation.country || orderLocation.country_name || null;
                const orderCity = orderLocation.city || orderLocation.city_name || null;

                if (!orderCountry && !orderCity) {
                    return null;
                }

                return {
                    country: orderCountry || '—',
                    city: orderCity || ''
                };
            },

            triggerMigration() {
                if (this.migrationLoading) {
                    return;
                }

                this.migrationLoading = true;
                this.migrationFeedback = '';

                axios.post('/api/antifraud/migrate-logs')
                    .then(({ data }) => {
                        this.setMigrationFeedback(data.message ?? 'Migração iniciada com sucesso.');
                    })
                    .catch(() => {
                        this.setMigrationFeedback('Não foi possível iniciar a migração agora. Tente novamente em instantes.');
                    })
                    .finally(() => {
                        this.migrationLoading = false;
                    });
            },

            setMigrationFeedback(message) {
                if (this.migrationFeedbackTimeout) {
                    clearTimeout(this.migrationFeedbackTimeout);
                }

                this.migrationFeedback = message;
                this.migrationFeedbackTimeout = window.setTimeout(() => {
                    this.migrationFeedback = '';
                    this.migrationFeedbackTimeout = null;
                }, 8000);
            },

            loadVisitorContext() {
                const visitorIds = [...new Set(this.events.map(event => event.visitorId).filter(Boolean))];

                this.visitorContext = { sales: {}, visitor_data: {} };

                if (visitorIds.length === 0) {
                    return;
                }

                axios.post('/api/antifraud/context', { visitor_ids: visitorIds })
                    .then(({ data }) => {
                        const sales = data.sales ?? {};
                        const visitorDataRaw = data.visitor_data ?? {};
                        const visitorDataMap = {};

                        Object.entries(visitorDataRaw).forEach(([key, value]) => {
                            if (!value) {
                                return;
                            }

                            const visitorId = value.visitorId ?? value.visitor_id ?? (Number.isNaN(Number(key)) ? key : null);

                            if (!visitorId) {
                                return;
                            }

                            visitorDataMap[visitorId] = value;
                        });

                        this.visitorContext = {
                            sales,
                            visitor_data: visitorDataMap,
                        };
                    })
                    .catch(() => {
                        this.visitorContext = { sales: {}, visitor_data: {} };
                    });
            },

            removeFilter(filterKey) {
                if (!this.filters?.[filterKey]) {
                    return;
                }

                const updatedFilters = { ...this.filters };
                delete updatedFilters[filterKey];

                const { [filterKey]: _removed, ...remainingLabels } = this.filterLabels;
                this.filterLabels = remainingLabels;

                this.filters = updatedFilters;
                this.resetPaginationAndFetch();
            },

            fetch() {
                this.loading = true;

                const params = {
                    filter: JSON.stringify(this.filters),
                    page: this.pagination.current_page
                };

                // Busca os dados principais
                axios.get('/api/antifraud', { params })
                    .then(({ data }) => {
                        // Atualiza os eventos com os items do hits
                        this.events = data.hits.hits;

                        this.lastUpdatedAt = data.last_updated_at ?? null;

                        this.expandedSalesVisitors = {};
                        this.expandedCardEvents = {};

                        // Atualiza flag de próxima página
                        this.hasNextPage = data.hits.has_next_page;

                        this.loadVisitorContext();
                        
                        this.loading = false;
                    })
                    .catch(error => {
                        console.error('Erro ao carregar dados:', error);
                        this.lastUpdatedAt = null;
                        this.loading = false;
                    });

                // Busca as agregações separadamente
                this.fetchAggregations();
            },

            fetchAggregations() {
                const filterParams = { filter: JSON.stringify(this.filters) };

                // Busca agregação por dia (gráfico da esquerda)
                this.loadingDayChart = true;
                axios.get('/api/antifraud/aggregations/day', { params: filterParams })
                    .then(({ data }) => {
                        this.aggs = {
                            ...this.aggs,
                            day: data.buckets
                        };
                    })
                    .catch(error => {
                        console.error('Erro ao carregar agregação por dia:', error);
                    })
                    .finally(() => {
                        this.loadingDayChart = false;
                    });

                // Busca agregação por failed-check (gráfico da direita)
                this.loadingFailedCheckChart = true;
                axios.get('/api/antifraud/aggregations/failed-check', { params: filterParams })
                    .then(({ data }) => {
                        this.aggs = {
                            ...this.aggs,
                            by_failed_check: data.buckets
                        };
                    })
                    .catch(error => {
                        console.error('Erro ao carregar agregação por failed-check:', error);
                    })
                    .finally(() => {
                        this.loadingFailedCheckChart = false;
                    });
            },

            nextPage() {
                if (this.hasNextPage && !this.loading) {
                    this.pagination.current_page++;
                    this.fetch();
                }
            },

            prevPage() {
                if (this.pagination.current_page > 1 && !this.loading) {
                    this.pagination.current_page--;
                    this.fetch();
                }
            }
        }
    }
</script>
